import { useState, useEffect } from "react";
import { api } from "../App";
import { getApiPrefix } from '../utils/apiHelper';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Button } from "../components/ui/button";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";
import { TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, BarChart3, PieChart as PieIcon, Sliders, Truck, AlertTriangle, CheckCircle2, Wrench } from "lucide-react";
import { PageLoader } from "../components/ui/page-loader";
import { useTranslation } from "../contexts/I18nContext";

const MONTH_NAMES = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
const ABC_COLORS = { A: "#10b981", B: "#f59e0b", C: "#ef4444" };

const fmt = (val) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(val || 0);

export default function AnalyticsPage() {
  const { t } = useTranslation();
  return (
    <div className="space-y-5" data-testid="analytics-page">
      <h1 className="text-2xl font-bold tracking-tight">Analisi Avanzate</h1>
      <Tabs defaultValue="yoy" className="w-full">
        <TabsList className="grid w-full grid-cols-5 h-auto">
          <TabsTrigger value="yoy" className="text-xs py-2">Anno su Anno</TabsTrigger>
          <TabsTrigger value="abc" className="text-xs py-2">Analisi ABC</TabsTrigger>
          <TabsTrigger value="aging" className="text-xs py-2">Aging Report</TabsTrigger>
          <TabsTrigger value="whatif" className="text-xs py-2">What-If</TabsTrigger>
          <TabsTrigger value="maintenance" className="text-xs py-2">Manutenzione</TabsTrigger>
        </TabsList>

        <TabsContent value="yoy"><YoYTab /></TabsContent>
        <TabsContent value="abc"><ABCTab /></TabsContent>
        <TabsContent value="aging"><AgingTab /></TabsContent>
        <TabsContent value="whatif"><WhatIfTab /></TabsContent>
        <TabsContent value="maintenance"><MaintenanceTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function YoYTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`${getApiPrefix()}/analytics/yoy-comparison?anno=${anno}`)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [anno]);

  if (loading) return <PageLoader variant="section" />;
  if (!data) return <p>Errore nel caricamento</p>;

  const chartData = data.corrente.mensile.map((m, i) => ({
    nome: MONTH_NAMES[m.mese - 1],
    [`Ricavi ${anno}`]: m.ricavi,
    [`Ricavi ${anno - 1}`]: data.precedente.mensile[i]?.ricavi || 0,
    [`Costi ${anno}`]: Math.abs(m.costi),
    [`Costi ${anno - 1}`]: Math.abs(data.precedente.mensile[i]?.costi || 0),
  }));

  const VarBadge = ({val}) => (
    <span className={`inline-flex items-center text-xs font-semibold ${val >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
      {val >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {val > 0 ? '+' : ''}{val?.toFixed(1)}%
    </span>
  );

  return (
    <div className="space-y-5 pt-4" data-testid="yoy-tab">
      <div className="flex items-center gap-3">
        <Select value={String(anno)} onValueChange={v => setAnno(Number(v))}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>{[2026, 2025, 2024].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">vs {anno - 1}</span>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {[
          { label: "Ricavi", cur: data.corrente.ricavi, prev: data.precedente.ricavi, var: data.variazioni.ricavi, color: "emerald" },
          { label: "Costi", cur: Math.abs(data.corrente.costi), prev: Math.abs(data.precedente.costi), var: data.variazioni.costi, color: "red" },
          { label: "Margine", cur: data.corrente.margine, prev: data.precedente.margine, var: data.variazioni.margine, color: "blue" },
        ].map((kpi, i) => (
          <Card key={i}>
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-muted-foreground uppercase">{kpi.label}</p>
              <p className="text-2xl font-bold mt-1">{fmt(kpi.cur)}</p>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-muted-foreground">Anno prec: {fmt(kpi.prev)}</span>
                <VarBadge val={kpi.var} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Confronto Mensile Ricavi</CardTitle></CardHeader>
        <CardContent>
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="nome" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `€${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={v => fmt(v)} />
                <Legend />
                <Bar dataKey={`Ricavi ${anno}`} fill="#10b981" radius={[3,3,0,0]} />
                <Bar dataKey={`Ricavi ${anno - 1}`} fill="#86efac" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ABCTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [tipo, setTipo] = useState("fornitori");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`${getApiPrefix()}/analytics/abc-analysis?tipo=${tipo}`)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [tipo]);

  if (loading) return <PageLoader variant="section" />;
  if (!data) return null;

  return (
    <div className="space-y-5 pt-4" data-testid="abc-tab">
      <div className="flex items-center gap-3">
        <Select value={tipo} onValueChange={setTipo}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="fornitori">Fornitori</SelectItem>
            <SelectItem value="clienti">Clienti</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">Totale: {fmt(data.totale_generale)}</span>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {Object.entries(data.riepilogo).map(([cls, info]) => (
          <Card key={cls} className={`border-l-4`} style={{ borderLeftColor: ABC_COLORS[cls] }}>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <Badge style={{ backgroundColor: ABC_COLORS[cls], color: 'white' }}>Classe {cls}</Badge>
                  <p className="text-2xl font-bold mt-2">{info.count}</p>
                  <p className="text-xs text-muted-foreground">{info.pct_soggetti}% dei {tipo}</p>
                </div>
                <p className="text-sm text-muted-foreground">{cls === 'A' ? '80% fatturato' : cls === 'B' ? '15% fatturato' : '5% fatturato'}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Dettaglio {tipo === 'fornitori' ? 'Fornitori' : 'Clienti'}</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b">
                <th className="text-left py-2 px-2">{t('common.company')}</th>
                <th className="text-right py-2 px-2">{t('invoices.title')}</th>
                <th className="text-right py-2 px-2">{t('common.total')}</th>
                <th className="text-right py-2 px-2">%</th>
                <th className="text-right py-2 px-2">Cum.</th>
                <th className="text-center py-2 px-2">Classe</th>
              </tr></thead>
              <tbody>
                {data.items.slice(0, 20).map((item) => (
                  <tr key={`${item.ragione_sociale}-${item.totale}`} className="border-b hover:bg-muted/30">
                    <td className="py-2 px-2 font-medium max-w-[250px] truncate">{item.ragione_sociale}</td>
                    <td className="text-right py-2 px-2">{item.num_fatture}</td>
                    <td className="text-right py-2 px-2 font-semibold">{fmt(item.totale)}</td>
                    <td className="text-right py-2 px-2">{item.percentuale}%</td>
                    <td className="text-right py-2 px-2">{item.cumulativa}%</td>
                    <td className="text-center py-2 px-2"><Badge style={{ backgroundColor: ABC_COLORS[item.classe], color: 'white' }} className="text-xs">{item.classe}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AgingTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [tipo, setTipo] = useState("debiti");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`${getApiPrefix()}/analytics/aging-report?tipo=${tipo}`)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [tipo]);

  if (loading) return <PageLoader variant="section" />;
  if (!data) return null;

  const bucketLabels = { corrente: "Corrente", "1_30gg": "1-30gg", "31_60gg": "31-60gg", "61_90gg": "61-90gg", oltre_90gg: "Oltre 90gg" };
  const bucketColors = ["#10b981", "#3b82f6", "#f59e0b", "#f97316", "#ef4444"];
  const chartData = Object.entries(data.fasce).map(([k, v], i) => ({ name: bucketLabels[k], totale: v.totale, count: v.count, fill: bucketColors[i] }));

  return (
    <div className="space-y-5 pt-4" data-testid="aging-tab">
      <div className="flex items-center gap-3">
        <Select value={tipo} onValueChange={setTipo}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="debiti">Debiti (Fornitori)</SelectItem>
            <SelectItem value="crediti">Crediti (Clienti)</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">Totale: {fmt(data.totale_generale)}</span>
      </div>

      <div className="grid gap-4 md:grid-cols-5">
        {Object.entries(data.fasce).map(([k, v], i) => (
          <Card key={k} className="border-t-4" style={{ borderTopColor: bucketColors[i] }}>
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-xs text-muted-foreground">{bucketLabels[k]}</p>
              <p className="text-xl font-bold mt-1">{fmt(v.totale)}</p>
              <p className="text-xs text-muted-foreground">{v.count} documenti</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="pt-5">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `€${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={v => fmt(v)} />
                <Bar dataKey="totale" name="Importo">
                  {chartData.map((e) => <Cell key={`cell-${e.name}`} fill={e.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function WhatIfTab() {
  const { t } = useTranslation();
  const [params, setParams] = useState({ var_ricavi: 0, var_costi: 0, var_personale: 0 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const simulate = () => {
    setLoading(true);
    const q = new URLSearchParams(params).toString();
    api.get(`${getApiPrefix()}/analytics/what-if?${q}`)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { simulate(); }, []);

  return (
    <div className="space-y-5 pt-4" data-testid="whatif-tab">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><Sliders className="h-4 w-4" />Parametri Simulazione</CardTitle></CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { key: "var_ricavi", label: "Variazione Ricavi" },
              { key: "var_costi", label: "Variazione Costi" },
              { key: "var_personale", label: "Variazione Personale" },
            ].map(p => (
              <div key={p.key} className="space-y-2">
                <div className="flex justify-between">
                  <Label className="text-sm">{p.label}</Label>
                  <span className={`text-sm font-bold ${params[p.key] >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{params[p.key] > 0 ? '+' : ''}{params[p.key]}%</span>
                </div>
                <Input type="range" min={-50} max={50} step={5} value={params[p.key]}
                  onChange={e => setParams({...params, [p.key]: Number(e.target.value)})}
                  className="w-full" />
              </div>
            ))}
          </div>
          <Button onClick={simulate} className="mt-4" disabled={loading}>{loading ? 'Calcolo...' : 'Simula Scenario'}</Button>
        </CardContent>
      </Card>

      {data && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Situazione Attuale</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { label: "Ricavi", val: data.base.ricavi },
                  { label: "Costi", val: Math.abs(data.base.costi) },
                  { label: "Personale", val: data.base.personale },
                  { label: "Margine", val: data.base.margine, bold: true },
                ].map((r) => (
                  <div key={r.label} className={`flex justify-between ${r.bold ? 'font-bold text-lg border-t pt-2' : ''}`}>
                    <span>{r.label}</span><span>{fmt(r.val)}</span>
                  </div>
                ))}
                <p className="text-sm text-muted-foreground">Margine: {data.base.margine_pct}%</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-blue-200 bg-blue-50/20">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-blue-700">Scenario Simulato</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { label: "Ricavi", val: data.simulazione.ricavi, delta: data.delta.ricavi },
                  { label: "Costi", val: Math.abs(data.simulazione.costi), delta: data.delta.costi },
                  { label: "Personale", val: data.simulazione.personale, delta: data.delta.personale },
                  { label: "Margine", val: data.simulazione.margine, delta: data.delta.margine, bold: true },
                ].map((r) => (
                  <div key={r.label} className={`flex justify-between items-center ${r.bold ? 'font-bold text-lg border-t pt-2' : ''}`}>
                    <span>{r.label}</span>
                    <div className="text-right">
                      <span>{fmt(r.val)}</span>
                      <span className={`ml-2 text-xs ${r.delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        ({r.delta >= 0 ? '+' : ''}{fmt(r.delta)})
                      </span>
                    </div>
                  </div>
                ))}
                <p className={`text-sm font-semibold ${data.simulazione.margine_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  Margine: {data.simulazione.margine_pct}%
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function MaintenanceTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`${getApiPrefix()}/analytics/predictive-maintenance`)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoader variant="section" />;
  const veicoli = data?.veicoli || [];

  const urgencyConfig = {
    critico: { color: "bg-red-100 text-red-800", icon: AlertTriangle },
    urgente: { color: "bg-orange-100 text-orange-800", icon: AlertTriangle },
    normale: { color: "bg-blue-100 text-blue-800", icon: Wrench },
    ok: { color: "bg-green-100 text-green-800", icon: CheckCircle2 },
    sconosciuto: { color: "bg-gray-100 text-gray-800", icon: Truck },
  };

  return (
    <div className="space-y-5 pt-4" data-testid="maintenance-tab">
      <p className="text-sm text-muted-foreground">Previsione manutenzione basata su storico interventi, KM percorsi e intervalli medi.</p>
      
      {veicoli.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Nessun veicolo registrato</CardContent></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {veicoli.map((v, i) => {
            const cfg = urgencyConfig[v.urgenza] || urgencyConfig.sconosciuto;
            const Icon = cfg.icon;
            return (
              <Card key={v.veicolo || `veicolo-${i}`} className="hover:shadow-md transition-all">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold">{v.veicolo}</p>
                      <p className="text-xs text-muted-foreground mt-1">Media {v.km_medi_mese} km/mese</p>
                    </div>
                    <Badge className={cfg.color}><Icon className="h-3 w-3 mr-1" />{v.urgenza}</Badge>
                  </div>
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Prossima manut.:</span>
                      <span className="font-medium">{v.prossima_manutenzione_prevista}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Giorni rimanenti:</span>
                      <span className={`font-medium ${v.giorni_rimanenti < 0 ? 'text-red-600' : v.giorni_rimanenti < 30 ? 'text-orange-600' : ''}`}>{v.giorni_rimanenti}gg</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Costo medio:</span>
                      <span className="font-medium">{fmt(v.costo_medio_manutenzione)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Intervallo medio:</span>
                      <span className="font-medium">{v.intervallo_medio_gg}gg</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
